/**
 * Sports Realm — collection toolbar enhancement.
 *
 * The page works without this file: the toolbar is one GET <form> posting to the
 * collection URL, so every filter and sort choice is a real form submission and
 * the chips are real links. This script only removes the extra click:
 *   1. dropdown panels open/close (desktop)
 *   2. mobile filter/sort sheets
 *   3. auto-submit on change, so "View results" is not needed
 *   4. Load more appends the next page in place via the Section Rendering API
 *
 * No dependencies.
 */
(function () {
  'use strict';

  function init() {
    var form = document.querySelector('[data-sr-facets]');
    var section = document.querySelector('.sr-coll');
    if (!section) return;

    /* ---- 0. One active input set ----------------------------------------
       The desktop dropdowns and the mobile sheets deliberately render the same
       fields, so the sheet is not a second source of truth. Both live in one
       form though, so the hidden set would still be submitted — duplicating
       every filter param and every sort radio. Disabled controls are not
       submitted, so the off-screen set is disabled and kept in sync. */
    var mobileQuery = window.matchMedia('(max-width: 768px)');

    function syncScopes() {
      var active = mobileQuery.matches ? 'mobile' : 'desktop';
      section.querySelectorAll('[data-sr-scope]').forEach(function (box) {
        var off = box.getAttribute('data-sr-scope') !== active;
        box.querySelectorAll('input').forEach(function (input) {
          input.disabled = off;
        });
      });
    }

    syncScopes();
    mobileQuery.addEventListener('change', syncScopes);

    /* ---- 1. Auto-submit -------------------------------------------------- */
    if (form) {
      // Empty price bounds would otherwise submit as `...price.gte=&...lte=`,
      // leaving blank params in a URL the merchant may share.
      form.addEventListener('submit', function () {
        form.querySelectorAll('.sr-price-range input').forEach(function (input) {
          if (input.value === '') input.disabled = true;
        });
      });

      // Hidden only now that JS is running; without it the no-JS button stays.
      form.addEventListener('change', function (e) {
        if (!e.target.matches('input[type="checkbox"], input[type="radio"]')) return;
        form.requestSubmit ? form.requestSubmit() : form.submit();
      });

      // Price inputs: submit on commit rather than each keystroke.
      form.querySelectorAll('.sr-price-range input').forEach(function (input) {
        input.addEventListener('change', function () {
          form.requestSubmit ? form.requestSubmit() : form.submit();
        });
      });
    }

    /* ---- 2. Dropdowns ---------------------------------------------------- */
    var drops = Array.prototype.slice.call(section.querySelectorAll('[data-sr-drop]'));

    function closeDrops(except) {
      drops.forEach(function (d) {
        if (d === except) return;
        d.classList.remove('is-open');
        var b = d.querySelector('[data-sr-dropbtn]');
        if (b) b.setAttribute('aria-expanded', 'false');
      });
    }

    drops.forEach(function (drop) {
      var btn = drop.querySelector('[data-sr-dropbtn]');
      if (!btn) return;
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var open = drop.classList.contains('is-open');
        closeDrops();
        if (!open) {
          drop.classList.add('is-open');
          btn.setAttribute('aria-expanded', 'true');
        }
      });
    });

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-sr-panel]') || e.target.closest('[data-sr-dropbtn]')) return;
      closeDrops();
    });

    /* ---- 3. Mobile sheets ------------------------------------------------ */
    var overlay = section.querySelector('[data-sr-overlay]');
    var lastFocused = null;

    function openSheet(sheet) {
      if (!sheet || !overlay) return;
      lastFocused = document.activeElement;
      overlay.hidden = false;
      sheet.hidden = false;
      // Force a reflow so the transform transition runs; rAF does not fire in a
      // throttled tab and would leave the sheet parked off-screen.
      void sheet.offsetWidth;
      overlay.classList.add('is-open');
      sheet.classList.add('is-open');
      document.documentElement.style.overflow = 'hidden';
      var close = sheet.querySelector('[data-sr-close]');
      if (close) close.focus();
      var trigger = section.querySelector('[aria-controls="' + sheet.id + '"]');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
    }

    function closeSheets() {
      if (overlay) {
        overlay.classList.remove('is-open');
        overlay.hidden = true;
      }
      section.querySelectorAll('.sr-sheet').forEach(function (s) {
        if (!s.classList.contains('is-open')) return;
        s.classList.remove('is-open');
        var t = section.querySelector('[aria-controls="' + s.id + '"]');
        if (t) t.setAttribute('aria-expanded', 'false');
        var done = function () {
          s.hidden = true;
          s.removeEventListener('transitionend', done);
        };
        s.addEventListener('transitionend', done);
        setTimeout(done, 400);
      });
      document.documentElement.style.overflow = '';
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }

    section.querySelectorAll('[data-sr-open]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        openSheet(document.getElementById(btn.getAttribute('aria-controls')));
      });
    });

    section.querySelectorAll('[data-sr-close]').forEach(function (btn) {
      btn.addEventListener('click', closeSheets);
    });

    if (overlay) overlay.addEventListener('click', closeSheets);

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      closeDrops();
      closeSheets();
    });

    // Keep focus inside an open sheet.
    section.querySelectorAll('.sr-sheet').forEach(function (sheet) {
      sheet.addEventListener('keydown', function (e) {
        if (e.key !== 'Tab' || !sheet.classList.contains('is-open')) return;
        var items = sheet.querySelectorAll('a[href], button:not([disabled]), input:not([disabled])');
        if (!items.length) return;
        var first = items[0];
        var last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      });
    });

    /* ---- 4. Load more ---------------------------------------------------- */
    var grid = section.querySelector('[data-sr-grid]');
    var btn = section.querySelector('[data-sr-loadmore]');

    function bindLoadMore(button) {
      if (!button) return;
      button.addEventListener('click', function (e) {
        e.preventDefault();
        var url = button.getAttribute('href');
        if (!url) return;
        button.setAttribute('aria-busy', 'true');

        fetch(url)
          .then(function (r) {
            if (!r.ok) throw new Error(r.status);
            return r.text();
          })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var nextGrid = doc.querySelector('[data-sr-grid]');
            if (nextGrid && grid) {
              Array.prototype.slice.call(nextGrid.children).forEach(function (card) {
                grid.appendChild(document.importNode(card, true));
              });
            }
            var nextShowing = doc.querySelector('[data-sr-showing]');
            var showing = section.querySelector('[data-sr-showing]');
            if (nextShowing && showing) showing.textContent = nextShowing.textContent;

            var nextBtn = doc.querySelector('[data-sr-loadmore]');
            if (nextBtn) {
              button.setAttribute('href', nextBtn.getAttribute('href'));
              button.removeAttribute('aria-busy');
            } else {
              button.remove();
            }
            // Update the address bar so the deeper page is shareable/back-safe.
            if (window.history && window.history.replaceState) {
              window.history.replaceState({}, '', url);
            }
          })
          .catch(function () {
            // Fall back to a normal navigation rather than stranding the user.
            button.removeAttribute('aria-busy');
            window.location.href = url;
          });
      });
    }

    bindLoadMore(btn);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
